import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Avocado } from '../entities/avocado.js';
import { createAmbient } from '../entities/ambient.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs } from '../core/save.js';
import { Hoops } from '../minigames/hoops.js';

// Avocado Court — the outdoor half-court. Bread Boi free-roams (click to walk),
// then walks up to the shooting spot to play a timed basketball minigame, up to
// the Rec Center to head inside (workout equipment + future sports), or to the
// gate to go back to town. States: 'explore' → 'playing' → 'results'.

const ASPHALT = 0x5B6B72;
const SHOOT_SPOT = new THREE.Vector3(0, 0, -2);
const RIM = new THREE.Vector3(0, 3.0, -5.6);

export function createCourtScene(){
  return () => new CourtScene();
}

class CourtScene {
  constructor(){
    this.state = 'explore';
    this.hoops = null;
    this.panels = [];
    this.ballAnim = null;
    this.breadShootT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(C.sky);
    this.scene.fog = new THREE.Fog(C.sky, 26, 55);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, -1);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildHoop();
    this._buildRecCenter();
    this._buildGate();
    this._buildBall();

    this.avocados = [
      new Avocado({ scale: 1.0 }),
      new Avocado({ scale: 0.85, tone: 0x6BA83F })
    ];
    this.avocados[0].root.position.set(-3.4, 0, 0.5);
    this.avocados[0].root.rotation.y = 0.5;
    this.avocados[1].root.position.set(3.6, 0, 1.4);
    this.avocados[1].root.rotation.y = -0.6;
    this.avocados.forEach(a => this.scene.add(a.root));

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 1);
    this.scene.add(this.bread.root);

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, -1), radius: 16, butterflies: 4, birds: 2 });
    this.scene.add(this.ambient.group);

    // walk-up hotspots (explore mode)
    this.hotspots = [
      { pos: SHOOT_SPOT.clone(), r: 2.4, label: '🏀 shoot hoops', action: () => this._startHoops() },
      { pos: new THREE.Vector3(5.2, 0, -1.6), r: 2.2, label: 'enter the Rec Center', action: () => this.manager.goTo('reccenter', { from: 'court' }) },
      { pos: new THREE.Vector3(0, 0, 5), r: 2.4, label: '← back to town', action: () => this.manager.goTo('town', { from: 'court' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.75));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.9);
    sun.position.set(6, 14, 8); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 18;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    const grass = new THREE.Mesh(new THREE.CircleGeometry(26, 48), new THREE.MeshLambertMaterial({ color: C.grass }));
    grass.rotation.x = -Math.PI/2; grass.receiveShadow = true;
    this.scene.add(grass);

    // asphalt court slab
    const court = new THREE.Mesh(new THREE.PlaneGeometry(9, 11), new THREE.MeshLambertMaterial({ color: ASPHALT }));
    court.rotation.x = -Math.PI/2; court.position.set(0, 0.01, -1); court.receiveShadow = true;
    this.scene.add(court);

    // painted lines
    const line = (w, d, x, z) => box(w, 0.02, d, C.cream, x, 0.03, z, this.scene);
    line(9, 0.12, 0, 3.9);                 // baseline (behind shooter)
    line(0.12, 11, -4.4, -1); line(0.12, 11, 4.4, -1);   // sidelines
    // the key (paint) near the hoop
    line(3.2, 0.1, 0, -2);                 // free-throw line
    line(0.1, 3.6, -1.6, -3.8); line(0.1, 3.6, 1.6, -3.8);
    line(3.2, 0.1, 0, -5.5);
    // center + free-throw arcs
    const ring = (rIn, rOut, x, z) => {
      const m = new THREE.Mesh(new THREE.RingGeometry(rIn, rOut, 40), new THREE.MeshBasicMaterial({ color: C.cream, side: THREE.DoubleSide }));
      m.rotation.x = -Math.PI/2; m.position.set(x, 0.03, z); this.scene.add(m);
    };
    ring(1.5, 1.62, 0, -2);                // free-throw circle
    // 3-point arc
    const arc = new THREE.Mesh(new THREE.RingGeometry(4.0, 4.12, 48, 1, Math.PI*0.15, Math.PI*0.7), new THREE.MeshBasicMaterial({ color: C.cream, side: THREE.DoubleSide }));
    arc.rotation.x = -Math.PI/2; arc.position.set(0, 0.03, -5.4); this.scene.add(arc);
  }

  _buildHoop(){
    cyl(0.12, 0.14, 3.4, C.crustDark, 0, 1.7, -6.3, this.scene, 12);      // pole
    const backboard = box(2.0, 1.3, 0.12, C.white, 0, 3.5, -6.0, this.scene);
    box(0.7, 0.5, 0.14, C.raspberry, 0, 3.2, -5.94, this.scene);          // target square
    // rim (torus) + net
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.05, 10, 24), new THREE.MeshLambertMaterial({ color: C.tangerine }));
    rim.rotation.x = Math.PI/2; rim.position.copy(RIM); rim.castShadow = true;
    this.scene.add(rim);
    const net = cyl(0.3, 0.18, 0.5, C.white, RIM.x, RIM.y - 0.25, RIM.z, this.scene, 14);
    net.material = new THREE.MeshLambertMaterial({ color: C.white, transparent: true, opacity: 0.55 });
  }

  _buildRecCenter(){
    const g = new THREE.Group(); g.position.set(7.2, 0, -1.8); this.scene.add(g);
    box(4.5, 3.4, 4.5, C.sprout, 0, 1.7, 0, g);                  // avocado-green body
    box(5.0, 0.5, 5.0, C.sproutDim, 0, 3.5, 0, g);              // roof
    box(1.4, 2.2, 0.3, C.crustDark, -2.25, 1.1, 0, g);         // door on the court-facing side
    box(1.0, 1.0, 0.15, C.breeze, -2.25, 2.4, 1.2, g);        // window
    box(1.0, 1.0, 0.15, C.breeze, -2.25, 2.4, -1.2, g);
    const sign = textSprite('REC CENTER', 30, '#FFF4DF', 3.4, 0.9);
    sign.position.set(6.0, 4.0, -1.8);           // world-space, above the court-facing door
    this.scene.add(sign);
  }

  _buildGate(){
    cyl(0.15, 0.15, 3.2, C.rye, -2.2, 1.6, 5.2, this.scene, 8);
    cyl(0.15, 0.15, 3.2, C.rye, 2.2, 1.6, 5.2, this.scene, 8);
    box(5.0, 0.4, 0.3, C.rye, 0, 3.2, 5.2, this.scene);
    const sign = textSprite('← to Toast Town', 26, '#6B4A2F', 3.4, 0.9);
    sign.position.set(0, 3.9, 5.2); this.scene.add(sign);
  }

  _buildBall(){
    this.ball = sphere(0.22, 0xE07B39, 0, -5, 0, this.scene, 16);
    this.ball.visible = false;
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.72];
    this._toExplore(true);
  }

  exit(){
    this._teardownGame();
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _toExplore(first){
    this.state = 'explore';
    this._clearPanels();
    this.bread.resetLimbs();
    this.input.onTap = (ndc, ray) => this._exploreTap(ray);
    HUD.setSubtitle('avocado court');
    if (first) HUD.say('Avocado Court! Click to walk. Step up to the line to shoot hoops, or head into the Rec Center.');
    HUD.setHint('click to walk · walk up to the line, the rec center, or the gate');
    HUD.hideHintSoon(7000);
  }

  _exploreTap(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    if (gp.length() > 24) gp.setLength(23.5);
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  // ---- basketball ----
  _startHoops(){
    this.state = 'playing';
    this._clearPanels();
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.setHint(''); HUD.hideHint();
    HUD.say('');

    this.bread.stop();
    this.bread.root.position.copy(SHOOT_SPOT);
    this.bread.root.rotation.y = Math.PI;          // face the hoop (-z)
    this.bread.facing = Math.PI;

    this.hoops = new Hoops({
      onShot: (r) => this._shootBall(r),
      onFinish: (r) => this._results(r)
    });
    this.hoops.mount();
    this.hoops.begin();

    const q = this._panel('<button class="cafe-quit">✕ end</button>', 'bare');
    q.querySelector('.cafe-quit').onclick = () => this.hoops && this.hoops._finish();
  }

  _shootBall(r){
    this.breadShootT = 0.45;                        // shooting pose
    this.bread.drawFace('effort');
    const from = new THREE.Vector3(0, 2.5, -2.35);
    const to = r.made
      ? new THREE.Vector3(0, 2.5, RIM.z)             // through the hoop
      : new THREE.Vector3((Math.random() < 0.5 ? 1 : -1) * 0.7, 2.95, RIM.z + 0.4); // clank off the rim
    const apex = new THREE.Vector3((from.x + to.x)/2, 4.7, (from.z + to.z)/2);
    this.ball.visible = true;
    this.ballAnim = { t: 0, dur: 0.72, from, apex, to, made: r.made };
    if (r.made) this.avocados.forEach(a => a.cheer());
  }

  _updateBall(dt){
    const b = this.ballAnim; if (!b) return;
    b.t += dt;
    const k = Math.min(1, b.t / b.dur);
    const u = 1 - k;
    // quadratic bezier arc from → apex → to
    this.ball.position.set(
      u*u*b.from.x + 2*u*k*b.apex.x + k*k*b.to.x,
      u*u*b.from.y + 2*u*k*b.apex.y + k*k*b.to.y,
      u*u*b.from.z + 2*u*k*b.apex.z + k*k*b.to.z
    );
    if (k >= 1){
      if (b.made && !b.dropped){
        // drop straight through the net
        b.dropped = true; b.t = 0; b.dur = 0.3;
        b.from = this.ball.position.clone(); b.apex = this.ball.position.clone(); b.apex.y -= 0.4;
        b.to = new THREE.Vector3(RIM.x, 1.2, RIM.z);
      } else {
        this.ball.visible = false; this.ballAnim = null;
      }
    }
  }

  _results(r){
    this.state = 'results';
    this._teardownGame();
    this.bread.resetLimbs(); this.bread.drawFace('grin');
    if (r.crumbs > 0) addCrumbs(r.crumbs);
    HUD.refreshCrumbs();

    const pct = r.attempts ? Math.round(r.makes / r.attempts * 100) : 0;
    const grade = pct >= 80 ? 'S' : pct >= 65 ? 'A' : pct >= 50 ? 'B' : pct >= 30 ? 'C' : 'D';
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Ballgame!</h2>
        <div class="cafe-stats">
          <div><b>${r.makes}</b><span>makes</span></div>
          <div><b>${r.swishes}</b><span>swish</span></div>
          <div><b>${r.bestStreak}</b><span>best streak</span></div>
          <div><b>${pct}%</b><span>shooting</span></div>
        </div>
        <p class="cafe-payout">🍞 +${r.crumbs} crumbs</p>
        <button class="cafe-btn again">↻ shoot again</button>
        <button class="cafe-btn ghost leave">← back to the court</button>
      </div>`);
    p.querySelector('.again').onclick = () => this._startHoops();
    p.querySelector('.leave').onclick = () => this._toExplore(false);
  }

  _teardownGame(){
    if (this.hoops){ this.hoops.unmount(); this.hoops = null; }
    this.ball.visible = false; this.ballAnim = null;
    this.breadShootT = 0;
  }

  // ---- panels (reuse the shared .cafe-* game UI) ----
  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  update(dt){
    this.ambient.update(dt);
    this.avocados.forEach(a => a.update(dt));
    this._updateBall(dt);

    if (this.state === 'playing'){
      this.hoops.update(dt);
      // shooting pose: arms up on release, then settle
      if (this.breadShootT > 0){
        this.breadShootT -= dt;
        const up = -2.1;
        this.bread.armL.shoulder.rotation.x = up;
        this.bread.armR.shoulder.rotation.x = up;
      } else {
        this.bread.armL.shoulder.rotation.x *= (1 - Math.min(1, dt*6));
        this.bread.armR.shoulder.rotation.x *= (1 - Math.min(1, dt*6));
      }
      // locked side-on shooting camera: off to the +x side so Bread Boi is in
      // profile (not blocking the rim) and the ball arc reads across the screen
      this.camera.position.set(6.4, 3.4, -1.2);
      this.camera.lookAt(-0.3, 2.4, -4.2);
      return;
    }

    // explore / results: free-roam camera following Bread Boi
    if (this.state === 'explore') this.bread.update(dt);

    let near = null, nd = Infinity;
    if (this.state === 'explore'){
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        if (this.nearSpot !== near){
          this.nearSpot = near;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 - 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
